/* eslint-disable require-await */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable jsx-a11y/label-has-associated-control */

import Container from '@brands/Components/Container/Container';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { listDependents } from '@brands/services/identity/listDependents';
import { Dependent } from '@brands/services/identity/types/Dependent';
import { selectAuth } from '@brands/store/selectors/auth';
import zIndex from '@mui/material/styles/zIndex';
import React, { useEffect, useMemo, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Modal from 'react-modal';

import { addPlanMember, getShareLink } from '../../../../services/patient_plan/services';
import { GroupMember, GroupSummary } from '../../../../services/patient_plan/types/types';
import { fullName, initials } from '../../assets/helpers';
import styles from './styles.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  group: GroupSummary;
  onGroupUpdated?: (next: GroupSummary) => void;
  onAddNewDependent?: () => void;
};

const AddMembersModal: React.FC<Props> = ({ isOpen, onClose, group, onGroupUpdated, onAddNewDependent }) => {
  const { userInfo } = useAppSelector(selectAuth);
  const isMobile = useWindowSize().width < 768;

  const [dependents, setDependents] = useState<Array<Dependent>>([]);
  const [loading, setLoading] = useState(false);

  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const remainingSeats = group?.remaining_slots ?? 0;
  const existingIds = useMemo(
    () => new Set((group?.members || []).filter((m: GroupMember) => m.active).map((m: GroupMember) => m.id)),
    [group?.members],
  );
  const alreadyInPlan = (dep: { id: number }) => existingIds.has(dep.id);

  const canInviteLink = remainingSeats > 0;

  const canAddMember = (userId: number) => {
    return group?.remaining_slots > 0 || group?.removed_users_with_credits.includes(userId);
  };

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    (async () => {
      setErrorMsg(null);
      setInviteUrl('');
      setCopyState('idle');
      setLoading(true);
      try {
        const deps = await listDependents(userInfo.id, { include_deleted: false });
        if (!mounted) return;
        setDependents(deps);
      } catch {
        if (mounted) setErrorMsg('Could not load dependents.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, userInfo.id]);

  useEffect(() => {
    if (!isOpen || !canInviteLink) return;
    let mounted = true;
    (async () => {
      try {
        setInviting(true);
        const { url } = await getShareLink(group.id);
        setInviteUrl(url);
      } catch {
        setInviteUrl('');
      } finally {
        if (mounted) setInviting(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOpen, canInviteLink, group?.id]);

  const handleAddDependent = async (dep: { id: number }) => {
    if (alreadyInPlan(dep)) return;
    try {
      setLoading(true);
      const updated = await addPlanMember(group.id, { user_id: dep.id });
      onGroupUpdated?.(updated);
    } catch {
      setErrorMsg('Could not add member to your plan.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1600);
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 1600);
    }
  };

  /* ---------- MOBILE: render inside Container instead of a portal/modal ---------- */
  if (isMobile) {
    if (!isOpen) return null;
    return (
      <Container
        patientDashboard
        isSidenavVisible
        childClassName={styles.mobileDashboard}
        containerStyle={{
          justifyContent: 'start',
          overflowY: 'scroll',
          zIndex: zIndex.drawer + 1,
        }}
      >
        <div className={styles.mobileRoot}>
          <button className={styles.backButton} onClick={onClose} aria-label="Back" type="button">
            <MdKeyboardArrowLeft />
            Back
          </button>

          <header className={styles.header}>
            <h2 className="fs-unmask">Add members to your plan</h2>
            <p className={styles.subtitle}>
              You can invite up to {remainingSeats} {remainingSeats === 1 ? 'member' : 'members'}
            </p>
          </header>

          <p className={styles.hint}>
            If you have family under the age of 18, you can add them to your plan as a dependent.
          </p>

          <div className={styles.list}>
            {loading && <div className={styles.loading}>Loading…</div>}
            {!loading &&
              dependents.map((d) => (
                <div key={d.id} className={styles.row}>
                  <div className={styles.info}>
                    <div className={styles.avatar}>
                      <span>{initials(d.full_name, d.email)}</span>
                    </div>
                    <div className={styles.name}>{fullName(d)}</div>
                  </div>
                  <div className={styles.action}>
                    {alreadyInPlan(d) ? (
                      <span className={styles.addedBadge}>Added</span>
                    ) : (
                      <button
                        type="button"
                        className={styles.addBtn}
                        disabled={!canAddMember(d.id)}
                        onClick={() => handleAddDependent(d)}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              ))}

            <button type="button" className={styles.addNew} onClick={onAddNewDependent} disabled={remainingSeats <= 0}>
              <span className={styles.addIcon}>+</span>
              <span> Add New Dependent</span>
            </button>
          </div>

          <div className={styles.inviteBlock}>
            <div className={styles.inviteText}>Or, share the link to invite friends or family over the age of 18.</div>

            <div className={styles.linkPill} role="group" aria-label="Invite link">
              <input
                className={styles.pillInput}
                value={inviteUrl}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                placeholder={inviting ? 'Generating link…' : 'Invite link unavailable'}
                aria-readonly="true"
              />
              <button
                type="button"
                className={`${styles.pillBtn} ${copyState === 'copied' ? styles.pillBtnSuccess : ''}`}
                disabled={!inviteUrl}
                onClick={handleCopy}
              >
                {copyState === 'copied' ? 'Copied' : 'Copy'}
              </button>
            </div>

            {errorMsg && <div className={styles.error}>{errorMsg}</div>}
          </div>
        </div>
      </Container>
    );
  }

  /* ---------- DESKTOP: keep your existing react-modal ---------- */
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <button className={styles.closeBtn} onClick={onClose} aria-label="Close" type="button">
        <AiOutlineClose />
      </button>

      <header className={styles.header}>
        <h2 className="fs-unmask">Add members to your plan</h2>
        <p className={styles.subtitle}>
          You can invite up to {remainingSeats} {remainingSeats === 1 ? 'member' : 'members'}
        </p>
      </header>

      <p className={styles.hint}>
        If you have family under the age of 18, you can add them to your plan as a dependent.
      </p>

      <div className={styles.list}>
        {loading && <div className={styles.loading}>Loading…</div>}
        {!loading &&
          dependents.map((d) => (
            <div key={d.id} className={styles.row}>
              <div className={styles.info}>
                <div className={styles.avatar}>
                  <span>{initials(d.full_name, d.email)}</span>
                </div>
                <div className={styles.name}>{fullName(d)}</div>
              </div>
              <div className={styles.action}>
                {alreadyInPlan(d) ? (
                  <span className={styles.addedBadge}>Added</span>
                ) : (
                  <button
                    type="button"
                    className={styles.addBtn}
                    disabled={!canAddMember(d.id)}
                    onClick={() => handleAddDependent(d)}
                  >
                    Add
                  </button>
                )}
              </div>
            </div>
          ))}

        <button type="button" className={styles.addNew} onClick={onAddNewDependent} disabled={remainingSeats <= 0}>
          <span className={styles.addIcon}>+</span>
          <span> Add New Dependent</span>
        </button>
      </div>

      <div className={styles.inviteBlock} aria-live="polite">
        <div className={styles.inviteText}>Or, share the link to invite friends or family over the age of 18.</div>

        <div className={styles.linkPill} role="group" aria-label="Invite link">
          <input
            className={styles.pillInput}
            value={inviteUrl}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            placeholder={inviting ? 'Generating link…' : 'Invite link unavailable'}
            aria-readonly="true"
          />
          <button
            type="button"
            className={`${styles.pillBtn} ${copyState === 'copied' ? styles.pillBtnSuccess : ''}`}
            disabled={!inviteUrl}
            onClick={handleCopy}
          >
            {copyState === 'copied' ? 'Copied' : 'Copy'}
          </button>
        </div>

        {errorMsg && <div className={styles.error}>{errorMsg}</div>}
      </div>
    </Modal>
  );
};

export default AddMembersModal;
